'use client';

import Link from 'next/link';
import { useEffect, useRef } from 'react';

import { useGTMPromotionBanner } from '@/fo/api/gtm';
import ArrowIcon from '@/fo/icons/base/icons/arrow24.svg';
import BiodermaLogo from '@/fo/icons/base/logos/bioderma.svg';
import EsthedermLogo from '@/fo/icons/base/logos/esthederm-new.svg';
import EtatPurLogo from '@/fo/icons/base/logos/etat-pur.svg';
import { useMediaQuery } from '@/fo/ui/mod/media/query';

import { styles } from './UiKitBannerBrandVideo.styles';

type NativeElement = React.HTMLAttributes<HTMLDivElement>;

const logoMap = {
  bioderma: BiodermaLogo,
  etatPur: EtatPurLogo,
  esthederm: EsthedermLogo
};

type Link = { label: string; href: string; target?: boolean };

type Video = {
  desktop: string;
  mobile: string;
};

export type BannerVideo = {
  slug: 'bioderma' | 'etatPur' | 'esthederm';
  video: Video;
  link: Link;
  promotionName?: string;
  creativeName?: string;
};

export interface UiKitBannerBrandVideoProps {
  bannerVideo: BannerVideo;
  onClick?: NativeElement['onClick'];
}

export function UiKitBannerBrandVideo({
  bannerVideo,
  ...props
}: UiKitBannerBrandVideoProps) {
  const slots = styles({});
  const BrandLogo = logoMap[bannerVideo.slug];

  const brandColorClasses = {
    bioderma: 'text-brand-bioderma',
    esthederm: 'text-brand-esthederm',
    etatPur: 'text-black'
  }[bannerVideo.slug];

  const isMobile = useMediaQuery('md', 'max');

  const forwardVideoRef = useRef<HTMLVideoElement>(null);
  const reverseVideoRef = useRef<HTMLVideoElement>(null);
  const { ref, trackSelectPromotion } = useGTMPromotionBanner({
    creativeName:
      bannerVideo.creativeName ||
      bannerVideo.video.desktop ||
      bannerVideo.video.mobile,
    promotionId: bannerVideo.link.href,
    promotionName: bannerVideo.promotionName || bannerVideo.link.label,
    source: 'static',
    index: 1
  });

  useEffect(() => {
    const forwardVideo = forwardVideoRef.current;
    const reverseVideo = reverseVideoRef.current;

    if (!forwardVideo || !reverseVideo) return;

    const initVideos = () => {
      [forwardVideo, reverseVideo].forEach((video) => {
        video.muted = true;
        video.playsInline = true;
        video.preload = 'auto';
      });

      reverseVideo.currentTime = reverseVideo.duration || 1;
      reverseVideo.style.display = 'none';
    };

    const handleForwardEnd = () => {
      forwardVideo.style.display = 'none';
      reverseVideo.style.display = 'block';
      reverseVideo.currentTime = reverseVideo.duration || 1;
      reverseVideo.play().catch(() => {
        setTimeout(() => {
          forwardVideo.currentTime = 0;
          forwardVideo.play().catch(console.error); // eslint-disable-line no-console
        }, 500);
      });
    };

    const handleReverseEnd = () => {
      reverseVideo.style.display = 'none';
      forwardVideo.style.display = 'block';
      forwardVideo.currentTime = 0;
      forwardVideo.play().catch(() => {
        setTimeout(() => {
          reverseVideo.currentTime = reverseVideo.duration || 1;
          reverseVideo.play().catch(console.error); // eslint-disable-line no-console
        }, 500);
      });
    };

    initVideos();
    forwardVideo.addEventListener('ended', handleForwardEnd);
    reverseVideo.addEventListener('ended', handleReverseEnd);

    forwardVideo.play().catch(() => {
      const handleUserInteraction = () => {
        forwardVideo.play().catch(console.error); // eslint-disable-line no-console
        document.removeEventListener('click', handleUserInteraction);
      };
      document.addEventListener('click', handleUserInteraction);
    });

    return () => {
      forwardVideo.removeEventListener('ended', handleForwardEnd);
      reverseVideo.removeEventListener('ended', handleReverseEnd);
      forwardVideo.pause();
      reverseVideo.pause();
    };
  }, [isMobile]);

  return (
    <div
      ref={ref}
      className={slots.base()}
      {...props}
    >
      <video
        ref={!isMobile ? forwardVideoRef : null}
        className={slots.video({ className: 'hidden md:block' })}
        muted
        playsInline
        preload="auto"
        width={1440}
        height={400}
      >
        <source src={bannerVideo.video.desktop} />
      </video>

      <video
        ref={!isMobile ? reverseVideoRef : null}
        className={slots.video({ className: 'hidden md:block' })}
        muted
        playsInline
        preload="auto"
        width={1440}
        height={400}
      >
        <source src={bannerVideo.video.desktop} />
      </video>
      {bannerVideo.video.mobile && (
        <>
          <video
            ref={isMobile ? forwardVideoRef : null}
            className={slots.video({ className: 'block md:hidden' })}
            muted
            playsInline
            preload="auto"
            width={375}
            height={400}
          >
            <source src={bannerVideo.video.mobile} />
          </video>
          <video
            ref={isMobile ? reverseVideoRef : null}
            className={slots.video({ className: 'block md:hidden' })}
            muted
            playsInline
            preload="auto"
            width={375}
            height={400}
          >
            <source src={bannerVideo.video.mobile} />
          </video>
        </>
      )}

      <div className={slots.wrap()}>
        <div className={slots.body()}>
          <BrandLogo
            width={315}
            height={90}
            className={slots.brandLogo({ className: brandColorClasses })}
          />
          <Link
            href={bannerVideo.link.href}
            className={slots.link()}
            target={bannerVideo.link.target ? '_blank' : '_self'}
            onClick={trackSelectPromotion}
          >
            {bannerVideo.link.label}
            <span className={slots.linkArrow()}>
              <ArrowIcon />
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
}
